/**
 * TutorialCard — compact line-playback control (BETA lives on the Plan tab).
 *
 * Minimal 2-line control LOCKED just above the bottom-right EdgeActions
 * stack — it is NOT anchored to the canvas node. Playback already selects
 * and centres each node (and its full detail shows in the drawer), so the
 * card only needs to say where you are and move you along:
 *
 *   row 1:  LINE NAME · 2/7            ‹  ›   ✕
 *   row 2:  Guard Pull → Closed Guard
 *
 * Current element is bright, the next one is dimmed, so it's always clear
 * which is which. Deliberately translucent so it hides as little of the
 * map as possible.
 */
import { useMemo } from 'react';
import { createPortal } from 'react-dom';
import { orderLineNodes } from '../data/line-path';
import { useAppStore } from '../state/store';
import type { Counter, Move, Position } from '../types/domain';
import type { LineNode } from '../types/gameplan';
import { useViewport } from './useViewport';
import { FONT, GOLD, INK, INK3, LINE2, RADIUS, hexA } from './tokens';

type Props = {
  positions: Position[];
  moves: Move[];
  counters: Counter[];
};

function refName(
  ref: LineNode['ref'],
  positions: Position[],
  moves: Move[],
  counters: Counter[],
): string {
  if (ref.kind === 'position')
    return positions.find((p) => p.id === ref.id)?.name ?? ref.id;
  if (ref.kind === 'move')
    return moves.find((m) => m.id === ref.id)?.name ?? ref.id;
  return counters.find((c) => c.id === ref.id)?.name ?? ref.id;
}

export function TutorialCard({ positions, moves, counters }: Props) {
  const tutorial = useAppStore((s) => s.tutorial);
  const gameplans = useAppStore((s) => s.gameplans);
  const next = useAppStore((s) => s.tutorialNext);
  const prev = useAppStore((s) => s.tutorialPrev);
  const end = useAppStore((s) => s.endTutorial);
  const { isMobile, isPortrait } = useViewport();
  const bottomSheet = isMobile && isPortrait;

  const line = useMemo(
    () =>
      tutorial
        ? gameplans.flatMap((g) => g.lines).find((l) => l.id === tutorial.lineId) ??
          null
        : null,
    [tutorial, gameplans],
  );
  const ordered = useMemo(() => (line ? orderLineNodes(line) : []), [line]);

  if (!tutorial || !line || ordered.length === 0) return null;

  const step = Math.min(tutorial.step, ordered.length - 1);
  const node = ordered[step];
  const nextNode = ordered[step + 1] ?? null;
  const atStart = step === 0;
  const atEnd = step >= ordered.length - 1;

  // Sit just above the EdgeActions stack (40px tall) in the bottom-right.
  const posStyle = bottomSheet
    ? { right: 14, bottom: 'calc(var(--bjjmap-sheet-height, 0px) + 54px)' }
    : { right: 'calc(var(--bjjmap-drawer-width, 0px) + 8px)', bottom: 66 };

  return createPortal(
    <div
      style={{
        position: 'fixed',
        ...posStyle,
        zIndex: 36,
        // Shrink to the content rather than a fixed slab — the row 2
        // "current → next" line usually sets the width; long names cap
        // out and ellipsis. Kills the big empty gap on short lines.
        width: 'fit-content',
        maxWidth: 'min(340px, calc(100vw - 28px))',
        background: 'rgba(18,22,33,0.6)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        border: `1px solid ${LINE2}`,
        borderRadius: RADIUS.md,
        boxShadow: '0 10px 30px rgba(0,0,0,0.45)',
        padding: '8px 10px',
        color: INK,
        fontFamily: FONT.ui,
        transition:
          'right 220ms cubic-bezier(0.2,0.8,0.2,1), bottom 220ms cubic-bezier(0.2,0.8,0.2,1)',
      }}
      role="dialog"
      aria-label="Line playback"
    >
      {/* Row 1 — line name + progress · prev / next · close. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span
          style={{
            fontFamily: FONT.mono,
            fontSize: 10.5,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: INK3,
            flex: 1,
            minWidth: 0,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {line.name} · {step + 1}/{ordered.length}
        </span>
        <IconBtn onClick={prev} disabled={atStart} label="Previous">
          <path d="M11.5 4 L6.5 9 L11.5 14" />
        </IconBtn>
        <IconBtn onClick={next} disabled={atEnd} label="Next" accent>
          <path d="M6.5 4 L11.5 9 L6.5 14" />
        </IconBtn>
        <IconBtn onClick={end} label="Close playback">
          <path d="M4 4 L14 14 M14 4 L4 14" />
        </IconBtn>
      </div>

      {/* Row 2 — current → next, current bright, next dimmed. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          marginTop: 5,
          fontSize: 12.5,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
        }}
      >
        <span
          style={{
            fontWeight: 600,
            color: INK,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            flexShrink: 1,
          }}
        >
          {refName(node.ref, positions, moves, counters)}
        </span>
        {nextNode && (
          <>
            <span style={{ color: GOLD, flexShrink: 0 }}>→</span>
            <span
              style={{
                color: INK3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                flexShrink: 1,
              }}
            >
              {refName(nextNode.ref, positions, moves, counters)}
            </span>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

function IconBtn({
  children,
  onClick,
  disabled,
  label,
  accent,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  label: string;
  accent?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      style={{
        width: 26,
        height: 26,
        flexShrink: 0,
        padding: 0,
        borderRadius: 7,
        border: `1px solid ${disabled ? 'transparent' : LINE2}`,
        background: accent && !disabled ? hexA(GOLD, 0.14) : 'transparent',
        color: disabled ? INK3 : accent ? GOLD : INK,
        opacity: disabled ? 0.4 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <svg
        width="15"
        height="15"
        viewBox="0 0 18 18"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        {children}
      </svg>
    </button>
  );
}
